import React, { useState, useEffect } from 'react';
import { Printer, AlertCircle, Award, ToggleLeft, ToggleRight, ChevronDown, Clock } from 'lucide-react';
import api from '../../api';

const GRADE_COLOR = {
  A1: '#16a34a', B2: '#2563eb', B3: '#2563eb',
  C4: '#d97706', C5: '#d97706', C6: '#d97706',
  D7: '#ea580c', E8: '#ea580c', F9: '#dc2626',
};

export default function StudentReportCardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allTerms, setAllTerms] = useState([]);   // [{year, terms:[]}]
  const [selectedTermId, setSelectedTermId] = useState('');
  const [cumulative, setCumulative] = useState(false);

  useEffect(() => {
    api.get('/academics/all-terms').then(d => {
      if (d.success && d.data?.length) {
        setAllTerms(d.data);
        // Default to current term: find current year's current term
        api.get('/academics/current-session').then(s => {
          if (s.success && s.data?.term) {
            setSelectedTermId(s.data.term.id);
            setCumulative((s.data.term.name || '').toLowerCase().includes('third'));
          } else {
            // Fall back to first term in list
            const first = d.data[0]?.terms?.[0];
            if (first) { setSelectedTermId(first.id); setCumulative((first.name || '').toLowerCase().includes('third')); }
          }
        }).catch(() => {
          const first = d.data[0]?.terms?.[0];
          if (first) { setSelectedTermId(first.id); setCumulative((first.name || '').toLowerCase().includes('third')); }
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedTermId === undefined) return;
    setLoading(true);
    setError('');
    api.get(`/exams/my-report-card?term_id=${selectedTermId}&cumulative=${cumulative}`)
      .then(d => { if (d.success) setData(d); })
      .catch(err => setError(err?.detail || 'Failed to load report card'))
      .finally(() => setLoading(false));
  }, [selectedTermId, cumulative]);

  const handleTermChange = (e) => {
    const termId = e.target.value;
    setSelectedTermId(termId);
    const term = allTerms.flatMap(g => g.terms).find(t => t.id === termId);
    setCumulative((term?.name || '').toLowerCase().includes('third'));
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto" data-testid="report-card-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print">
        <div>
          <h1 className="text-lg font-bold text-slate-900">My Report Card</h1>
          <p className="text-sm text-slate-500">Printable academic report</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {allTerms.length > 0 && (
            <div className="relative">
              <select value={selectedTermId} onChange={handleTermChange}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none appearance-none pr-8"
                data-testid="report-term-select">
                {allTerms.map(group => (
                  <optgroup key={group.year.id} label={group.year.name}>
                    {group.terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </optgroup>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}
          <button onClick={() => setCumulative(c => !c)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${
              cumulative ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
            }`} data-testid="cumulative-toggle">
            {cumulative ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            Cumulative
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 bg-indigo-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            data-testid="print-btn">
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 no-print">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3 no-print">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Cannot load report card</p>
            <p className="text-sm text-amber-700 mt-1">{error}</p>
          </div>
        </div>
      ) : data ? (
        <ReportCard data={data} />
      ) : null}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; margin: 0; }
          .report-card-wrapper { border: none !important; box-shadow: none !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
}

export function ReportCard({ data }) {
  const {
    student, school, term, academic_year, subjects, overall_average, position, total_in_class,
    attendance_rate, attendance_days_present, attendance_days_total,
    is_cumulative, terms: termList = [],
    assessment_config = [], class_teacher_comment, principal_comment, term_metadata = {}
  } = data;

  const overallGrade = overall_average >= 75 ? 'A1' : overall_average >= 70 ? 'B2'
    : overall_average >= 65 ? 'B3' : overall_average >= 60 ? 'C4'
    : overall_average >= 50 ? 'C6' : overall_average >= 45 ? 'D7'
    : overall_average >= 40 ? 'E8' : 'F9';

  const totalMax = assessment_config.reduce((s, c) => s + c.max_score, 0) || 100;

  return (
    <div className="report-card-wrapper bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="report-card-content">
      {/* School Header */}
      <div className="bg-[#0F1623] text-white text-center py-8 px-6 border-b-4 border-[#2563eb]">
        {school?.logo
          ? <img
              src={school.logo}
              alt={school.name || 'School logo'}
              crossOrigin="anonymous"
              className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-4 border-white/20 shadow-lg"
            />
          : <div className="w-20 h-20 rounded-full bg-[#2563eb]/30 mx-auto mb-3 flex items-center justify-center border-4 border-[#2563eb]/40 shadow-lg">
              <Award className="w-9 h-9 text-[#2563eb]" />
            </div>
        }
        <h2 className="text-2xl font-extrabold tracking-wide uppercase">{school?.name || 'SCHOOL NAME'}</h2>
        {school?.address && <p className="text-xs text-slate-400 mt-1">{school.address}{school.city ? `, ${school.city}` : ''}{school.state ? `, ${school.state}` : ''}</p>}
        {school?.phone && <p className="text-xs text-slate-500 mt-0.5">{school.phone}</p>}
        {school?.motto && <p className="text-xs italic text-slate-300 mt-1 font-light">"{school.motto}"</p>}
        <div className="mt-4 inline-block border border-[#2563eb]/50 rounded-full px-5 py-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#2563eb]">
            {is_cumulative ? 'Annual Cumulative Report Card' : 'Student Academic Report Card'}
          </p>
        </div>
      </div>

      {/* Term Banner */}
      <div className="bg-indigo-50 border-b border-indigo-100 px-6 py-2 text-center">
        <p className="text-sm font-semibold text-indigo-700">
          {is_cumulative
            ? `${termList.map(t => t.name).join(' · ')} · Cumulative`
            : `${term?.name || '—'} · ${academic_year?.name || '—'}`}
        </p>
      </div>

      {/* Student Info + Passport Photo */}
      <div className="flex gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
        {/* Info grid */}
        <div className="flex-1 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <InfoRow label="Student Name" value={student ? `${student.first_name} ${student.last_name}` : '—'} />
          <InfoRow label="Admission No." value={student?.admission_number || '—'} />
          <InfoRow label="Class" value={student?.class_name || '—'} />
          <InfoRow label="Academic Year" value={academic_year?.name || '—'} />
          <InfoRow label="Gender" value={student?.gender || '—'} />
          <InfoRow label="Date of Birth" value={student?.date_of_birth || '—'} />
        </div>
        {/* Passport photo */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div className="w-24 h-28 border-2 border-slate-300 overflow-hidden bg-slate-100 flex items-center justify-center">
            {student?.avatar
              ? <img
                  src={student.avatar}
                  alt={`${student.first_name} ${student.last_name}`}
                  crossOrigin="anonymous"
                  className="w-full h-full object-cover object-top"
                />
              : <span className="text-3xl font-bold text-slate-300">
                  {`${student?.first_name?.[0] || ''}${student?.last_name?.[0] || ''}`}
                </span>
            }
          </div>
          <p className="text-[9px] text-slate-400 mt-1 uppercase tracking-wider text-center">Passport Photograph</p>
        </div>
      </div>

      {/* Results Table */}
      <div className="px-6 pt-5 pb-2">
        <h3 className="text-xs font-bold uppercase text-slate-500 tracking-widest mb-3">Academic Performance</h3>
        {subjects.length === 0 ? (
          <div className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-xl p-5 my-2" data-testid="results-not-published">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-800">Results Not Yet Published</p>
              <p className="text-sm text-amber-700 mt-0.5">Results for this term have not been approved by admin yet. Please check back later.</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {is_cumulative
              ? <CumulativeTable subjects={subjects} termList={termList} />
              : <SingleTermTable subjects={subjects} assessmentConfig={assessment_config} totalMax={totalMax} />
            }
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-slate-100">
        <SummaryBox label="Overall Average" value={overall_average != null ? `${overall_average}%` : '—'} color="#4f46e5" />
        <SummaryBox label="Overall Grade" value={subjects.length ? overallGrade : '—'} color={GRADE_COLOR[overallGrade] || '#64748b'} />
        <SummaryBox label="Class Position" value={position ? `${position} / ${total_in_class}` : '—'} color="#0891b2" />
        <SummaryBox
          label="Attendance"
          value={attendance_days_present != null
            ? `${attendance_days_present} / ${term_metadata?.days_school_opened || attendance_days_total || '?'} days`
            : attendance_rate != null ? `${attendance_rate}%` : '—'}
          color="#16a34a"
        />
      </div>

      {/* Comments Section */}
      <div className="px-6 py-4 border-t border-slate-200 grid grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-bold uppercase text-slate-500 tracking-wide mb-2">Class Teacher's Remark</p>
          {class_teacher_comment
            ? <p className="text-sm text-slate-700 italic border border-slate-200 rounded-lg p-2.5 bg-slate-50">{class_teacher_comment}</p>
            : <div className="border-b-2 border-dashed border-slate-200 h-8 mt-1" />}
          <div className="mt-3 border-b border-slate-300 pb-1">
            <p className="text-[10px] text-slate-400">Signature & Date</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase text-slate-500 tracking-wide mb-2">Principal's Remark</p>
          {principal_comment
            ? <p className="text-sm text-slate-700 italic border border-slate-200 rounded-lg p-2.5 bg-slate-50">{principal_comment}</p>
            : <div className="border-b-2 border-dashed border-slate-200 h-8 mt-1" />}
          <div className="mt-3 border-b border-slate-300 pb-1">
            <p className="text-[10px] text-slate-400">Signature & Date</p>
          </div>
        </div>
      </div>

      {/* Term Footer */}
      {(term_metadata?.term_closes || term_metadata?.next_term_resumes) && (
        <div className="px-6 py-3 border-t border-slate-100 bg-indigo-50 grid grid-cols-2 gap-4 text-sm">
          {term_metadata.term_closes && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Term Closes:</span>
              <span className="font-semibold text-slate-700">{term_metadata.term_closes}</span>
            </div>
          )}
          {term_metadata.next_term_resumes && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase">Next Term Resumes:</span>
              <span className="font-semibold text-slate-700">{term_metadata.next_term_resumes}</span>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="bg-[#0F1623] border-t border-slate-700 px-6 py-2 text-center">
        <p className="text-[10px] text-slate-400">
          Generated by EDUVA · {new Date().toLocaleDateString('en-NG', { day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </div>
    </div>
  );
}

function SingleTermTable({ subjects, assessmentConfig, totalMax }) {
  const hasComponents = assessmentConfig.length > 0 &&
    subjects.some(s => s.components && s.components.some(c => c.score !== null));

  if (!hasComponents) {
    // Fallback: simple table
    return (
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
        <thead>
          <tr className="bg-slate-50">
            {['Subject', 'Total', 'Grade', 'Remark'].map(h => (
              <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-600 border-b border-slate-200 uppercase tracking-wide">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {subjects.length === 0 ? (
            <tr><td colSpan={4} className="text-center text-slate-400 py-6 text-sm">No results recorded for this term</td></tr>
          ) : subjects.map((s, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <td className="px-3 py-2.5 font-semibold text-slate-700">{s.subject}</td>
              <td className="px-3 py-2.5 font-bold text-slate-800">{s.total_score ?? s.percentage ?? '—'}</td>
              <td className="px-3 py-2.5 font-bold text-lg" style={{ color: GRADE_COLOR[s.grade] || '#64748b' }}>{s.grade}</td>
              <td className="px-3 py-2.5 text-slate-500">{s.remark}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-slate-800 text-white">
          <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide border-r border-slate-600">Subject</th>
          {assessmentConfig.map(c => (
            <th key={c.name} className="text-center px-2 py-2.5 text-xs font-bold uppercase tracking-wide border-r border-slate-600 whitespace-nowrap">
              {c.name}<br /><span className="text-slate-400 font-normal">/{c.max_score}</span>
            </th>
          ))}
          <th className="text-center px-3 py-2.5 text-xs font-bold uppercase tracking-wide border-r border-slate-600 bg-slate-700">
            Total<br /><span className="text-slate-400 font-normal">/{totalMax}</span>
          </th>
          <th className="text-center px-2 py-2.5 text-xs font-bold uppercase tracking-wide border-r border-slate-600">Grade</th>
          <th className="text-center px-2 py-2.5 text-xs font-bold uppercase tracking-wide">Remark</th>
        </tr>
      </thead>
      <tbody>
        {subjects.length === 0 ? (
          <tr><td colSpan={assessmentConfig.length + 4} className="text-center text-slate-400 py-6">No results recorded for this term</td></tr>
        ) : subjects.map((s, i) => (
          <tr key={i} className={`border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
            <td className="px-3 py-2.5 font-semibold text-slate-700 border-r border-slate-100">{s.subject}</td>
            {assessmentConfig.map(comp => {
              const compData = (s.components || []).find(c => c.name === comp.name);
              return (
                <td key={comp.name} className="px-2 py-2.5 text-center border-r border-slate-100">
                  {compData?.score != null
                    ? <span className="font-bold text-slate-800">{compData.score}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
              );
            })}
            <td className="px-3 py-2.5 text-center font-bold text-lg text-slate-900 border-r border-slate-100 bg-slate-50">{s.total_score ?? '—'}</td>
            <td className="px-2 py-2.5 text-center font-extrabold text-base border-r border-slate-100" style={{ color: GRADE_COLOR[s.grade] || '#64748b' }}>{s.grade}</td>
            <td className="px-2 py-2.5 text-center text-xs text-slate-500">{s.remark}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CumulativeTable({ subjects, termList }) {
  return (
    <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-slate-800 text-white">
          <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-wide border-r border-slate-600">Subject</th>
          {termList.map(t => (
            <th key={t.id} className="text-center px-3 py-2.5 text-xs font-bold uppercase tracking-wide border-r border-slate-600 whitespace-nowrap">
              {t.name.replace(' Term', '')}
            </th>
          ))}
          <th className="text-center px-3 py-2.5 text-xs font-bold text-indigo-300 uppercase tracking-wide border-r border-slate-600">Cum Avg</th>
          <th className="text-center px-2 py-2.5 text-xs font-bold uppercase tracking-wide">Grade</th>
        </tr>
      </thead>
      <tbody>
        {subjects.length === 0 ? (
          <tr><td colSpan={termList.length + 3} className="text-center text-slate-400 py-6">No results recorded</td></tr>
        ) : subjects.map((s, i) => (
          <tr key={i} className={`border-b border-slate-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
            <td className="px-3 py-2.5 font-semibold text-slate-700 border-r border-slate-100">{s.subject}</td>
            {s.terms.map((t, j) => (
              <td key={j} className="px-3 py-2.5 text-center text-slate-700 border-r border-slate-100 font-medium">{t.score ?? '—'}</td>
            ))}
            <td className="px-3 py-2.5 text-center font-extrabold text-indigo-700 text-base border-r border-slate-100">{s.cumulative_avg ?? '—'}</td>
            <td className="px-2 py-2.5 text-center font-extrabold text-base" style={{ color: GRADE_COLOR[s.grade] || '#64748b' }}>{s.grade}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-slate-500 min-w-[110px] font-medium">{label}:</span>
      <span className="font-bold text-slate-800">{value}</span>
    </div>
  );
}

function SummaryBox({ label, value, color }) {
  return (
    <div className="text-center p-3 rounded-xl bg-slate-50 border border-slate-100">
      <p className="text-[10px] text-slate-500 mb-0.5 uppercase font-semibold tracking-wide">{label}</p>
      <p className="text-sm font-extrabold" style={{ color }}>{value}</p>
    </div>
  );
}
